import { feedbackStore, PRIMARY_SITES, REVIEWER_ROLES, COLLATERAL_TYPES } from '../data/feedback-store.js';
import { conceptsData } from '../data/concepts-data.js';

export function renderBallotView() {
  const state = feedbackStore.getState();
  const { profile, ballot, conceptsFeedback } = state;
  const isProfileValid = feedbackStore.isProfileValid();
  const progressStats = feedbackStore.getProgressStats();

  // Get active rank order [rank1, rank2, rank3]
  const rankedIds = feedbackStore.getRankedConceptIds();
  
  const rankMeta = [
    { rank: 1, label: '1ST CHOICE — PRIMARY RECOMMENDATION', medal: '🥇', color: '#FFB300', border: '3px solid #FFB300' },
    { rank: 2, label: '2ND CHOICE — SECONDARY ALTERNATIVE', medal: '🥈', color: '#C0C0C0', border: '3px solid #C0C0C0' },
    { rank: 3, label: '3RD CHOICE — LEAST PREFERRED', medal: '🥉', color: '#CD7F32', border: '3px solid #CD7F32' }
  ];

  return `
    <section class="view-section" id="view-ballot">
      <!-- Hero Header -->
      <div class="hero-card" style="background: linear-gradient(135deg, rgba(30, 27, 75, 0.6) 0%, rgba(15, 23, 42, 0.9) 100%); border-left: 6px solid var(--accent-primary);">
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: var(--space-md);">
          <div>
            <span class="hero-tag" style="background: rgba(99, 102, 241, 0.2); color: #a5b4fc;">
              Phase 06 Decision Synthesis • Staff &amp; Community Feedback
            </span>
            <h2 style="font-size: var(--text-4xl); margin-top: 8px; margin-bottom: 8px; color: var(--text-primary);">
              Official Brand Review Ballot
            </h2>
            <p style="max-width: 820px; font-size: var(--text-lg); margin-bottom: 0; color: var(--text-secondary);">
              Review visual brand samples, drag and drop to rank your 1st, 2nd, and 3rd choices, identify campus collateral needs, and save your feedback locally for UVC leadership.
            </p>
          </div>

          <div style="text-align: right; background: var(--bg-surface-elevated); padding: var(--space-md) var(--space-lg); border-radius: var(--radius-md); border: 1px solid var(--border-subtle);">
            <div class="mono" style="font-size: 11px; color: var(--text-tertiary); text-transform: uppercase;">Completion Status</div>
            <div style="font-size: var(--text-2xl); font-weight: 800; color: ${progressStats.percentage === 100 ? '#00C853' : 'var(--accent-primary)'};">
              ${progressStats.percentage}%
            </div>
            <div style="font-size: 11px; color: var(--text-secondary);">${progressStats.completedCount} of ${progressStats.totalCount} sections complete</div>
          </div>
        </div>
      </div>

      <!-- Submission Success Banner (if already submitted) -->
      ${ballot.isSubmitted ? `
        <div class="card" style="background: rgba(0, 200, 83, 0.12); border: 1px solid #00C853; margin-bottom: var(--space-2xl); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--space-md);">
          <div style="display: flex; align-items: center; gap: var(--space-md);">
            <span style="font-size: 32px;">🎉</span>
            <div>
              <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                <h3 style="margin: 0; color: #00C853; font-size: var(--text-lg);">Official Ballot Recorded &amp; Saved!</h3>
                <span class="mono" style="background: rgba(0, 200, 83, 0.2); color: #00E676; padding: 2px 8px; border-radius: var(--radius-sm); font-size: 11px;">
                  ${ballot.serverConfirmed ? '✓ Stored on werk-n-serv' : '✓ Saved Locally'}
                </span>
                ${ballot.submissionId ? `
                  <span class="mono" style="background: rgba(255,255,255,0.08); color: var(--text-secondary); padding: 2px 8px; border-radius: var(--radius-sm); font-size: 11px;">
                    Ref: ${ballot.submissionId}
                  </span>
                ` : ''}
              </div>
              <p style="margin: 4px 0 0 0; font-size: var(--text-sm); color: var(--text-primary);">
                Saved on <strong>${ballot.submittedAt || 'Today'}</strong> for <strong>${profile.name} (${profile.site} • ${profile.role})</strong>.
              </p>
            </div>
          </div>
          <div style="display: flex; gap: var(--space-sm); align-items: center; flex-wrap: wrap;">
            <button class="btn btn-secondary" id="btn-copy-summary" style="font-size: var(--text-xs); border-color: #00C853; color: var(--text-primary);">
              📋 Copy Submission Summary
            </button>
            <button class="btn btn-secondary" id="btn-reset-ballot" style="font-size: var(--text-xs); border-color: rgba(255, 61, 113, 0.5); color: #FF3D71;">
              🗑️ Clear / Reset Ballot
            </button>
          </div>
        </div>
      ` : ''}

      <!-- Section 1: Reviewer Identification Card -->
      <div class="card" style="margin-bottom: var(--space-2xl); border-left: 4px solid ${isProfileValid ? '#00C853' : '#FFB300'};">
        <div class="card-header" style="margin-bottom: var(--space-sm);">
          <div style="display: flex; align-items: center; gap: var(--space-sm);">
            <span style="font-size: 20px;">👤</span>
            <h3 class="card-title">1. Reviewer Profile (Required)</h3>
          </div>
          <button class="btn btn-secondary btn-sm" id="btn-edit-profile-ballot">
            ✏️ ${isProfileValid ? 'Edit Info' : 'Set Profile Info'}
          </button>
        </div>

        ${isProfileValid ? `
          <div class="grid-4" style="gap: var(--space-md); background: var(--bg-surface-elevated); padding: var(--space-md); border-radius: var(--radius-md);">
            <div>
              <div class="mono" style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">Full Name</div>
              <div style="font-size: var(--text-sm); font-weight: 700; color: var(--text-primary);">${profile.name}</div>
            </div>
            <div>
              <div class="mono" style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">Email</div>
              <div style="font-size: var(--text-sm); color: var(--text-secondary);">${profile.email}</div>
            </div>
            <div>
              <div class="mono" style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">Primary Campus (5 Sites)</div>
              <div style="font-size: var(--text-sm); font-weight: 700; color: var(--accent-primary);">${profile.site}</div>
            </div>
            <div>
              <div class="mono" style="font-size: 10px; color: var(--text-tertiary); text-transform: uppercase;">UVC Role</div>
              <div style="font-size: var(--text-sm); color: var(--text-primary);">${profile.role}</div>
            </div>
          </div>
        ` : `
          <div style="background: rgba(255, 179, 0, 0.1); border: 1px dashed #FFB300; padding: var(--space-md); border-radius: var(--radius-md); display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: var(--space-sm);">
            <div>
              <div style="font-weight: 700; color: #FFB300; font-size: var(--text-sm);">Profile Incomplete</div>
              <div style="font-size: var(--text-xs); color: var(--text-secondary);">Your Name, Email, Role, and Primary Site (HPW, Ravenswood, Wicker Park, River Forest, Online) are required to submit.</div>
            </div>
            <button class="btn btn-primary btn-sm" id="btn-prompt-set-profile" style="background: #FFB300; color: #000000; font-weight: 700;">
              Fill Profile Now &rarr;
            </button>
          </div>
        `}
      </div>

      <!-- Section 2: Visual Drag/Drop Ranked-Choice Concept Stacker -->
      <div class="card" style="margin-bottom: var(--space-2xl);">
        <div class="card-header" style="margin-bottom: var(--space-md);">
          <div>
            <h3 class="card-title">2. Ranked-Choice Concept Selection</h3>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: 0;">
              Drag and drop to stack your preferences, or use the <strong>▲ Up / ▼ Down</strong> buttons. Visual digital/social campaign samples are shown below for each direction.
            </p>
          </div>
          <span class="badge" style="background: rgba(99, 102, 241, 0.2); color: #a5b4fc;">
            ⠿ Drag &amp; Drop to Rank
          </span>
        </div>

        <div class="drag-stack-container" id="rank-drag-container" style="display: flex; flex-direction: column; gap: var(--space-lg);">
          ${rankedIds.map((conceptId, index) => {
            const concept = conceptsData.find(c => c.id === conceptId) || conceptsData[0];
            const meta = rankMeta[index];
            const socialMockup = concept.mockups.find(m => m.id.endsWith('-social')) || concept.mockups[concept.mockups.length - 1];

            return `
              <div 
                class="draggable-rank-card" 
                draggable="true" 
                data-rank-index="${index}" 
                data-concept-id="${concept.id}"
                id="rank-card-${concept.id}"
                style="border-left: ${meta.border}; background: var(--bg-surface-elevated); border-radius: var(--radius-lg); padding: var(--space-lg); border-top: 1px solid var(--border-subtle); border-right: 1px solid var(--border-subtle); border-bottom: 1px solid var(--border-subtle); transition: transform 0.2s ease, box-shadow 0.2s ease;"
              >
                <!-- Header with Medal & Reorder Controls -->
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--space-md); border-bottom: 1px solid var(--border-subtle); padding-bottom: var(--space-sm); flex-wrap: wrap; gap: var(--space-sm);">
                  <div style="display: flex; align-items: center; gap: 8px;">
                    <span style="font-size: 24px;">${meta.medal}</span>
                    <span class="mono" style="font-weight: 800; font-size: var(--text-xs); color: ${meta.color}; letter-spacing: 0.05em;">
                      ${meta.label}
                    </span>
                  </div>

                  <div style="display: flex; align-items: center; gap: 6px;">
                    <span class="mono drag-handle-hint" style="font-size: 11px; color: var(--text-tertiary); cursor: grab; padding: 4px 8px; background: rgba(255,255,255,0.05); border-radius: var(--radius-sm);">
                      ⠿ Drag to Move
                    </span>
                    <button 
                      type="button" 
                      class="btn btn-secondary btn-sm btn-move-rank-up" 
                      data-rank-index="${index}" 
                      ${index === 0 ? 'disabled style="opacity: 0.3; cursor: not-allowed;"' : ''}
                      title="Move Up"
                    >
                      ▲
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-secondary btn-sm btn-move-rank-down" 
                      data-rank-index="${index}" 
                      ${index === 2 ? 'disabled style="opacity: 0.3; cursor: not-allowed;"' : ''}
                      title="Move Down"
                    >
                      ▼
                    </button>
                  </div>
                </div>

                <!-- Concept Card Body with Digital/Social Visual Preview -->
                <div style="display: grid; grid-template-columns: 240px 1fr; gap: var(--space-lg); align-items: center;" class="rank-card-inner-grid">
                  <!-- Visual Reminder Thumbnail -->
                  <div 
                    style="position: relative; border-radius: var(--radius-md); overflow: hidden; background: #000; aspect-ratio: 4/3; cursor: pointer; border: 1px solid var(--border-strong); box-shadow: 0 4px 12px rgba(0,0,0,0.3);"
                    data-lightbox-src="${socialMockup.image}" 
                    data-lightbox-title="Concept 0${concept.number}: ${concept.name} — Social Campaign"
                    data-lightbox-desc="${socialMockup.description}"
                    title="Click to expand full image"
                  >
                    <img 
                      src="${socialMockup.image}" 
                      alt="${concept.name} Social Preview" 
                      style="width: 100%; height: 100%; object-fit: cover; transition: transform 0.3s ease;"
                      class="mockup-img"
                    />
                    <div style="position: absolute; bottom: 0; left: 0; right: 0; background: linear-gradient(to top, rgba(0,0,0,0.85), transparent); padding: 4px 8px; font-size: 10px; color: #FFF; display: flex; justify-content: space-between;">
                      <span class="mono">📱 Social Campaign Sample</span>
                      <span>🔍 Expand</span>
                    </div>
                  </div>

                  <!-- Concept Metadata & Highlights -->
                  <div style="display: flex; flex-direction: column; justify-content: space-between; height: 100%;">
                    <div>
                      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
                        <span style="width: 12px; height: 12px; border-radius: 50%; background: ${concept.colors[0].hex}; display: inline-block;"></span>
                        <h4 style="margin: 0; font-size: var(--text-xl); color: var(--text-primary);">
                          Concept 0${concept.number}: ${concept.name}
                        </h4>
                        <span class="hero-tag" style="margin: 0; padding: 2px 8px; font-size: 10px; background: rgba(255,255,255,0.08); color: var(--text-primary);">
                          ${concept.archetype}
                        </span>
                      </div>

                      <div style="font-size: var(--text-sm); font-weight: 700; color: ${concept.colors[0].hex}; margin-bottom: 6px;">
                        <em>"${concept.tagline}"</em>
                      </div>

                      <p style="font-size: var(--text-xs); color: var(--text-secondary); line-height: 1.6; margin-bottom: var(--space-sm);">
                        ${concept.summary}
                      </p>
                    </div>

                    <div style="display: flex; gap: var(--space-sm); flex-wrap: wrap;">
                      <span class="badge" style="background: rgba(255,255,255,0.05); color: var(--text-tertiary); font-size: 10px;">
                        Tone: ${concept.emotionalTone.split(',').slice(0, 3).join(', ')}
                      </span>
                      <button 
                        class="btn btn-secondary btn-sm" 
                        data-tab="concepts" 
                        data-concept="${concept.id}" 
                        style="font-size: 10px; padding: 2px 8px;"
                      >
                        🔍 Explore Full Assets &rarr;
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>

      <!-- Section 3: Live Scorecard Summary Matrix -->
      <div class="card" style="margin-bottom: var(--space-2xl);">
        <div class="card-header">
          <div>
            <h3 class="card-title">3. Your Rubric Evaluations Summary</h3>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: 0;">
              Live summary of the 1-5 ratings you provided across the 3 brand concepts.
            </p>
          </div>
          <button class="btn btn-secondary btn-sm" data-tab="concepts" style="font-size: 11px;">
            🔍 Edit Concept Ratings
          </button>
        </div>

        <div style="overflow-x: auto;">
          <table style="width: 100%; border-collapse: collapse; min-width: 600px; font-size: var(--text-xs);">
            <thead>
              <tr style="border-bottom: 2px solid var(--border-subtle); text-align: left; background: var(--bg-surface-elevated);">
                <th style="padding: 10px 12px; color: var(--text-tertiary);">Evaluation Dimension</th>
                <th style="padding: 10px 12px; color: #3B28CC;">Concept 01 (Activism)</th>
                <th style="padding: 10px 12px; color: #C85A32;">Concept 02 (Sanctuary)</th>
                <th style="padding: 10px 12px; color: #D4AF37;">Concept 03 (Neo-Liturgical)</th>
              </tr>
            </thead>
            <tbody>
              <tr style="border-bottom: 1px solid var(--border-subtle);">
                <td style="padding: 10px 12px; font-weight: 600; color: var(--text-primary);">⚡ Boldness &amp; Prophetic Justice</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-1']?.boldScore)}</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-2']?.boldScore)}</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-3']?.boldScore)}</td>
              </tr>
              <tr style="border-bottom: 1px solid var(--border-subtle);">
                <td style="padding: 10px 12px; font-weight: 600; color: var(--text-primary);">🌈 Inclusivity &amp; Radical Welcome</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-1']?.inclusiveScore)}</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-2']?.inclusiveScore)}</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-3']?.inclusiveScore)}</td>
              </tr>
              <tr style="border-bottom: 1px solid var(--border-subtle);">
                <td style="padding: 10px 12px; font-weight: 600; color: var(--text-primary);">🏙️ Cultural Relevance for Chicago</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-1']?.relevantScore)}</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-2']?.relevantScore)}</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-3']?.relevantScore)}</td>
              </tr>
              <tr style="border-bottom: 1px solid var(--border-subtle);">
                <td style="padding: 10px 12px; font-weight: 600; color: var(--text-primary);">📍 Fit for ${profile.site || 'Your Campus'}</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-1']?.siteFitScore)}</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-2']?.siteFitScore)}</td>
                <td style="padding: 10px 12px;">${formatScoreCell(conceptsFeedback['concept-3']?.siteFitScore)}</td>
              </tr>
              <tr style="background: rgba(255,255,255,0.02); font-weight: 700;">
                <td style="padding: 10px 12px; color: var(--text-primary);">⭐ Overall Concept Average</td>
                <td style="padding: 10px 12px; color: #3B28CC;">${calculateAvg(conceptsFeedback['concept-1'])}</td>
                <td style="padding: 10px 12px; color: #C85A32;">${calculateAvg(conceptsFeedback['concept-2'])}</td>
                <td style="padding: 10px 12px; color: #D4AF37;">${calculateAvg(conceptsFeedback['concept-3'])}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Section 4: Site Collateral Priority Matrix -->
      <div class="card" style="margin-bottom: var(--space-2xl);">
        <div class="card-header">
          <div>
            <h3 class="card-title">4. Urgent Collateral Priorities for ${profile.site || 'Your Campus'}</h3>
            <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: 0;">
              Which asset types does your site need most urgently upon brand rollout?
            </p>
          </div>
          <span class="mono" style="font-size: 11px; color: var(--text-tertiary);">Select all that apply</span>
        </div>

        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: var(--space-sm);">
          ${COLLATERAL_TYPES.map(item => {
            const isChecked = ballot.priorityCollateral?.includes(item);
            return `
              <label class="priority-checkbox-card ${isChecked ? 'active' : ''}">
                <input 
                  type="checkbox" 
                  class="priority-collateral-checkbox" 
                  data-collateral-item="${item}" 
                  ${isChecked ? 'checked' : ''} 
                  style="display: none;" 
                />
                <span class="custom-check-box">${isChecked ? '✓' : ''}</span>
                <span style="font-size: var(--text-xs); font-weight: 600; color: var(--text-primary);">${item}</span>
              </label>
            `;
          }).join('')}
        </div>
      </div>

      <!-- Section 5: Advice for Leadership & Pastoral Team -->
      <div class="card" style="margin-bottom: var(--space-2xl);">
        <h3 class="card-title" style="margin-bottom: var(--space-xs);">5. Pastoral Wisdom &amp; Open Feedback</h3>
        <p style="font-size: var(--text-xs); color: var(--text-secondary); margin-bottom: var(--space-md);">
          Any concluding thoughts, gut reactions, theological reflections, or suggestions for the Church Council and Staff?
        </p>

        <textarea 
          id="ballot-advice-input" 
          class="form-textarea" 
          placeholder="Share your overall synthesis, advice for implementation, hybrid combination ideas, or words of encouragement..."
          rows="4"
        >${ballot.adviceForLeadership || ''}</textarea>
      </div>

      <!-- Section 6: Streamlined Local Submission & Sharing -->
      <div class="card" style="background: linear-gradient(180deg, rgba(29, 33, 48, 0.95) 0%, rgba(20, 23, 34, 0.98) 100%); border: 2px solid var(--border-accent); padding: var(--space-2xl);">
        <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: var(--space-lg); margin-bottom: var(--space-lg);">
          <div>
            <h3 style="margin: 0 0 4px 0; font-size: var(--text-2xl); color: var(--text-primary);">
              Submit &amp; Save Your Official Ballot
            </h3>
            <p style="margin: 0; font-size: var(--text-sm); color: var(--text-secondary);">
              Your feedback is saved securely on this device. You can also copy a formatted summary report or email it directly to leadership.
            </p>
          </div>

          <!-- Primary Save / Submit Button -->
          <button 
            type="button" 
            class="btn btn-primary" 
            id="btn-submit-ballot"
            style="background: var(--accent-primary); color: #FFFFFF; font-size: var(--text-base); padding: 12px 28px; box-shadow: 0 4px 16px rgba(99, 102, 241, 0.4); font-weight: 700;"
            ${!isProfileValid ? 'disabled' : ''}
          >
            💾 Submit &amp; Save Official Ballot
          </button>
        </div>

        ${!isProfileValid ? `
          <div style="background: rgba(255, 61, 113, 0.1); border: 1px solid #FF3D71; padding: var(--space-sm) var(--space-md); border-radius: var(--radius-md); font-size: var(--text-xs); color: #FF3D71; margin-bottom: var(--space-lg);">
            ⚠️ Please complete your <strong>Name, Email, Role, and Primary Campus</strong> in Section 1 above before submitting.
          </div>
        ` : ''}

        <!-- Secondary Sharing & Copy Summary Options -->
        <div style="display: flex; gap: var(--space-md); flex-wrap: wrap; border-top: 1px solid var(--border-subtle); padding-top: var(--space-lg); justify-content: space-between; align-items: center;">
          <div style="display: flex; gap: var(--space-md); flex-wrap: wrap;">
            <button type="button" class="btn btn-secondary" id="btn-copy-markdown-summary" style="font-size: var(--text-xs); padding: 8px 18px;">
              📋 Copy Markdown Report to Clipboard
            </button>
            
            <button type="button" class="btn btn-secondary" id="btn-email-ballot" style="font-size: var(--text-xs); padding: 8px 18px;">
              ✉️ Email Ballot to Leadership (leaders@urbanvillagechurch.org)
            </button>
          </div>

          <button type="button" class="btn btn-secondary" id="btn-reset-all-data" style="font-size: var(--text-xs); padding: 8px 18px; color: #FF3D71; border-color: rgba(255, 61, 113, 0.4);">
            🗑️ Clear / Reset Responses
          </button>
        </div>
      </div>
    </section>
  `;
}

function formatScoreCell(score) {
  if (!score) return `<span style="color: var(--text-tertiary);">Unrated</span>`;
  const stars = '★'.repeat(score) + '☆'.repeat(5 - score);
  return `<span style="font-weight: 700; color: var(--text-primary);">${score}/5</span> <span style="color: #FFB300; font-size: 11px;">${stars}</span>`;
}

function calculateAvg(cf) {
  if (!cf) return '—';
  const scores = [cf.boldScore, cf.inclusiveScore, cf.relevantScore, cf.siteFitScore].filter(Boolean);
  if (scores.length === 0) return '—';
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  return `<strong>${avg.toFixed(1)}</strong> / 5.0`;
}
